import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Mail, ShieldCheck } from 'lucide-react';
import { api } from '../../services/api';
import { SignaturePad } from '../../components/SignaturePad';
import { ContractDocument } from './ContractView';

/**
 * Página pública /contrato?id=TOKEN — la abre el comprador desde el correo. Sin cuenta:
 * lee el contrato, pide un código de verificación a su correo y firma.
 */
export const ContractSignPage: React.FC = () => {
    const token = new URLSearchParams(window.location.search).get('id') || '';
    const [c, setC] = useState<any | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const [codeSent, setCodeSent] = useState<string | null>(null);
    const [code, setCode] = useState('');
    const [signer, setSigner] = useState('');
    const [signature, setSignature] = useState<string | null>(null);
    const [accept, setAccept] = useState(false);
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState('');

    const load = async () => {
        try { setC(await api.breeder.viewContract(token)); }
        catch (e: any) { setError(e.message || 'No se pudo abrir el contrato'); }
        finally { setLoading(false); }
    };
    useEffect(() => { if (token) load(); else { setError('El enlace del contrato no es válido.'); setLoading(false); } }, []);

    const sendCode = async () => {
        setBusy(true); setFormError('');
        try { const r = await api.breeder.requestContractCode(token); setCodeSent(r.sentTo); }
        catch (e: any) { setFormError(e.message); }
        finally { setBusy(false); }
    };

    const sign = async () => {
        setBusy(true); setFormError('');
        try {
            await api.breeder.signContractAsBuyer({ token, code, signer, signature, accept });
            await load();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e: any) { setFormError(e.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[#FEF3E8] pb-16">
            <style>{`@media print { body * { visibility: hidden; } .contract-doc, .contract-doc * { visibility: visible; } .contract-doc { position: absolute; left: 0; top: 0; width: 100%; border: 0; box-shadow: none; } }`}</style>
            <header className="bg-white border-b border-orange-100 print:hidden">
                <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
                    <img src="/logo.png" alt="Amigo Peludo" className="h-8 w-auto object-contain" />
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black text-slate-400 uppercase tracking-widest"><ShieldCheck size={13} className="text-emerald-500" /> Firma electrónica verificada por correo</span>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-5 pt-8">
                {loading ? <div className="py-24 text-center"><Loader2 className="animate-spin mx-auto text-orange-500" size={32} /></div>
                    : error ? (
                        <div className="bg-white rounded-[2rem] p-10 text-center shadow-sm border border-slate-100">
                            <p className="text-lg font-black text-slate-800 mb-2">No pudimos abrir el contrato</p>
                            <p className="text-sm text-slate-500">{error}</p>
                            <p className="text-xs text-slate-400 mt-4">Si crees que es un error, pídele al criadero que te reenvíe el enlace.</p>
                        </div>
                    ) : c && (
                        <div className="space-y-6">
                            {c.status === 'sent' && (
                                <div className="bg-white rounded-[2rem] p-6 border border-orange-100 print:hidden">
                                    <p className="text-sm text-slate-600 leading-relaxed"><strong>{c.kennelName}</strong> te envió este contrato para {c.petName}. Léelo completo y, si estás de acuerdo, fírmalo al final de la página.</p>
                                </div>
                            )}
                            {(c.status === 'expired') && <div className="bg-amber-50 border border-amber-200 rounded-[7px] p-5 text-sm font-bold text-amber-700">Este enlace venció. Pide al criadero que te envíe el contrato de nuevo.</div>}

                            <ContractDocument c={c} showPrint={c.status === 'signed'} />

                            {c.status === 'sent' && (
                                <div className="bg-white rounded-[2rem] p-6 sm:p-8 shadow-md border border-slate-100 print:hidden">
                                    <h3 className="text-lg font-black text-slate-900 mb-1">Firmar el contrato</h3>
                                    <p className="text-[12px] text-slate-400 font-medium mb-6">Verificamos que eres tú con un código que enviamos a {c.buyerEmail}.</p>

                                    {!codeSent ? (
                                        <button onClick={sendCode} disabled={busy} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-black text-[12px] uppercase tracking-widest disabled:opacity-50 transition-colors">
                                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Enviarme el código
                                        </button>
                                    ) : (
                                        <div className="space-y-5">
                                            <p className="text-[12px] font-bold text-emerald-600 flex items-center gap-2"><CheckCircle2 size={15} /> Código enviado a {codeSent}. Vence en 10 minutos. <button onClick={sendCode} disabled={busy} className="underline text-slate-400 hover:text-slate-600 ml-1">Reenviar</button></p>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Código de 6 dígitos</label>
                                                <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
                                                    className="w-full sm:w-56 px-4 py-3.5 bg-slate-50 border-2 border-slate-100 rounded-[7px] font-mono text-2xl tracking-[0.4em] text-center font-black focus:outline-none focus:border-orange-400" />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Tu nombre completo</label>
                                                <input value={signer} onChange={e => setSigner(e.target.value)} placeholder="Nombre y apellidos" className="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-100 rounded-[7px] font-bold focus:outline-none focus:border-orange-400" />
                                            </div>
                                            <SignaturePad onChange={setSignature} />
                                            <label className="flex items-start gap-3 text-[13px] text-slate-600 font-medium cursor-pointer">
                                                <input type="checkbox" checked={accept} onChange={e => setAccept(e.target.checked)} className="mt-1 w-4 h-4 accent-orange-500" />
                                                He leído el contrato, lo acepto y acepto firmarlo de forma electrónica.
                                            </label>
                                            {formError && <p className="text-xs font-bold text-rose-500 bg-rose-50 rounded-[7px] px-4 py-3">{formError}</p>}
                                            <button onClick={sign} disabled={busy || code.length !== 6 || signer.trim().length < 5 || !accept}
                                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 bg-slate-900 hover:bg-emerald-600 text-white rounded-full font-black text-[12px] uppercase tracking-widest disabled:opacity-40 transition-colors">
                                                {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Firmar contrato
                                            </button>
                                        </div>
                                    )}
                                    {!codeSent && formError && <p className="mt-4 text-xs font-bold text-rose-500 bg-rose-50 rounded-[7px] px-4 py-3">{formError}</p>}
                                </div>
                            )}
                        </div>
                    )}
            </main>
        </div>
    );
};

export default ContractSignPage;
